import { OrderItemMetadata } from '@/components/OrderItemMetadata';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useOrderDetails } from '@/hooks/useOrder';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatPrice } from '@/lib/format';

export function OrderConfirmationView() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();
  const { data: order, isLoading, error } = useOrderDetails(orderId!);

  if (isLoading) {
    return (
      <div className="container py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container py-8">
        <div className="text-red-500">Error loading order details</div>
      </div>
    );
  }

  // Nothing to ship when every line is an eSIM: no address to show.
  const isDeliveryFree = order.items.every(
    (item) => item.type === 'TRAVEL_ESIM',
  );

  return (
    <div className="min-h-screen bg-[#f7f7f8] py-4">
      <div className="mx-auto max-w-4xl">
        {/* Back button */}
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          {/* Success Header */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <CheckCircle2 className="h-16 w-16 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold">Commande confirmée !</h1>
            <p className="text-muted-foreground">
              Merci pour votre commande. Nous avons bien reçu votre demande.
            </p>
          </div>

          {/* Order Summary */}
          <div className="border-t pt-8">
            <h2 className="text-xl font-semibold mb-6">
              Récapitulatif de la commande
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Order Details */}
              <div className="space-y-4">
                <div>
                  <span className="block text-xs text-muted-foreground">
                    Numéro de commande
                  </span>
                  <span className="font-medium text-lg">
                    #{order.orderNumber}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-muted-foreground">
                    Date
                  </span>
                  <span className="font-medium">
                    {format(new Date(order.createdAt), 'PPP', { locale: fr })}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-muted-foreground">
                    Total
                  </span>
                  <span className="font-medium text-lg">
                    {formatPrice(order.total)}
                  </span>
                </div>
              </div>

              {/* Delivery Address (nothing to ship for an eSIM-only order) */}
              {!isDeliveryFree && (
              <div>
                <span className="block text-xs text-muted-foreground mb-2">
                  Adresse de livraison
                </span>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2">
                    <span className="font-medium">
                      {order.organization.name}
                    </span>
                    {order.deliveryAddress && !order.deliveryAddress?.isProfessional && (
                      <Badge variant="outline">Adresse personnelle</Badge>
                    )}
                    {order.deliveryAddress?.isProfessional && (
                      <div>Entreprise: {order.deliveryAddress?.companyName}</div>
                    )}
                    {order.deliveryAddress?.recipientName && (
                      <div>
                        Destinataire: {order.deliveryAddress?.recipientName}
                      </div>
                    )}
                    <div>{order.deliveryAddress?.street}</div>
                    <div>
                      {order.deliveryAddress?.zipCode}{' '}
                      {order.deliveryAddress?.city}
                    </div>
                    <div>{order.deliveryAddress?.country}</div>
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* Order Items */}
            <div className="mt-8">
              <span className="block text-xs text-muted-foreground mb-2">
                Articles commandés
              </span>
              <div className="divide-y rounded-lg border bg-gray-50">
                {order.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div className="flex-1">
                      <div className="font-medium">{item.name}</div>
                      {item.description && (
                        <div className="text-xs text-gray-500">
                          {item.description}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {item.quantity} ×{' '}
                        {formatPrice(item.price)}
                        {item.isRecurring ? (
                          <span className="text-blue-600 ml-1">/mois</span>
                        ) : (
                          <span className="text-gray-500 ml-1">à l'acte</span>
                        )}
                      </div>
                      <OrderItemMetadata item={item} orderId={order.id} />
                    </div>
                    <div className="flex flex-col items-end gap-2 ml-4">
                      <span className="font-semibold text-sm">
                        {formatPrice(item.total)}
                        {item.isRecurring ? (
                          <span className="text-blue-600 ml-1">/mois</span>
                        ) : (
                          <span className="text-gray-500 ml-1">à l'acte</span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totaux */}
            <div className="mt-6 space-y-2">
              {order.fixedTotal > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Total HT (à l'acte)
                  </span>
                  <span className="font-medium">
                    {formatPrice(order.fixedTotal - (order.shippingCost ?? 0))}
                  </span>
                </div>
              )}
              {order.recurringTotal > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Total HT/mois (forfaits)
                  </span>
                  <span className="font-medium">
                    {formatPrice(order.recurringTotal)}
                  </span>
                </div>
              )}
              {order.shippingCost && order.shippingCost > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Frais de port
                    {order.shippingMethod === 'LETTRE_SIMPLE'
                      ? ' (Lettre simple)'
                      : order.shippingMethod === 'TNT'
                      ? ' (TNT)'
                      : ''}
                  </span>
                  <span className="font-medium">
                    {formatPrice(order.shippingCost)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between border-t pt-2">
                <span className="font-medium">Total</span>
                <span className="text-lg font-bold">
                  {formatPrice(order.total)}
                </span>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="border-t pt-8">
            <h2 className="text-xl font-semibold mb-4">Prochaines étapes</h2>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Vous recevrez un email de confirmation avec tous les détails de
                votre commande.
              </p>
              <p className="text-muted-foreground">
                Notre équipe va traiter votre commande dans les plus brefs
                délais.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              variant="outline"
              onClick={() => navigate(`/o/${tenantSlug}/orders`)}
            >
              Voir mes commandes
            </Button>
            <Button onClick={() => navigate(`/o/${tenantSlug}/lines`)}>
              Retour à l'accueil
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
